import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useTranslate, useGetList } from 'react-admin';

interface BasicDriverInfoStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const BasicDriverInfoStep: React.FC<BasicDriverInfoStepProps> = ({
  onNext,
  onBack: _onBack,
  onClose,
  existingData,
  driver,
  isFirstStep: _isFirstStep,
  isLastStep: _isLastStep,
}) => {
  const translate = useTranslate();

  const [formData, setFormData] = useState({
    userId: '',
    profileState: 'in_progress',
    hasCanadaExperience: true,
    experienceRadius: '',
  });

  // Fetch users from API
  const { data: users = [], isLoading: usersLoading } = useGetList('users', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'email', order: 'ASC' },
    filter: {},
  });

  useEffect(() => {
    if (existingData) {
      setFormData(prev => ({ ...prev, ...existingData }));
    } else if (driver) {
      setFormData(prev => ({ ...prev, ...driver }));
    }
  }, [existingData, driver]);

  const experienceRadiusOptions = [
    { value: 'LOCAL', label: translate('geography.experience_radius.local') },
    { value: 'REGIONAL', label: translate('geography.experience_radius.regional') },
    { value: 'LONG_HAUL', label: translate('geography.experience_radius.long_haul') },
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onNext(formData);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'normal', mb: 4, textAlign: 'center' }}>
        {translate('wizard.steps.basic_info.title')}
      </Typography>

      <Grid container spacing={3}>
        {/* User Selection */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('wizard.steps.basic_info.select_user')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{translate('wizard.steps.basic_info.select_user')}</InputLabel>
            <Select
              value={formData.userId}
              label={translate('wizard.steps.basic_info.select_user')}
              onChange={e => handleInputChange('userId', e.target.value)}
              sx={{ backgroundColor: 'white' }}
              disabled={usersLoading}
            >
              {usersLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {translate('wizard.steps.basic_info.loading_users')}
                </MenuItem>
              ) : (
                users.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        {/* Experience Radius */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('wizard.steps.basic_info.experience_radius')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{translate('wizard.steps.basic_info.select_experience_level')}</InputLabel>
            <Select
              value={formData.experienceRadius}
              label={translate('wizard.steps.basic_info.select_experience_level')}
              onChange={e => handleInputChange('experienceRadius', e.target.value)}
              sx={{ backgroundColor: 'white' }}
            >
              {experienceRadiusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 6,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderColor: '#1976d2',
            color: '#1976d2',
          }}
        >
          {translate('common.form.close')}
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={!formData.userId || !formData.experienceRadius}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
            px: 4,
            py: 1.5,
          }}
        >
          {translate('wizard.navigation.save_and_continue')}
        </Button>
      </Box>
    </Box>
  );
};
